import React, { useState, useRef } from 'react';
import { getCookieId } from '../../../WebserverUser';
import { serviceRequest } from '../../../../shared/xomics-webserver-client';
import './query.css';
import BodyVisual from './bodyvisual';
import QueryResults from './QueryResults';

function Query() {
  const [searchState, setSearchState] = useState('initial');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [showLeftPanel, setShowLeftPanel] = useState(false);
  const searchInputRef = useRef(null);

  // State for UI feedback is fully restored
  const [isCached, setIsCached] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [estimatedTime, setEstimatedTime] = useState('');
  
  // Ref to hold the interval ID, preventing stale closures
  const loadingIntervalRef = useRef(null);

  const loadingMessages = [
    'Initializing VEP pipeline...',
    'Annotating with Ensembl...',
    'Cross-referencing databases (ClinVar, COSMIC)...',
    'Querying therapeutic knowledge bases...',
    'Synthesizing decision support summary...'
  ];
  
  const searchSuggestions = ['NM_004333.6:c.1799T>G', 'rs113488022', 'rs144496976', 'NM_005228.5:c.2235_2249del'];
  const recentSearches = ['rs80358983', 'rs121913529', 'rs121434568', 'rs1801133'];

  const allowedFormats = [
    { label: 'rsID', example: 'rs113488022' },
    { label: 'HGVS', example: 'NM_004333.6:c.1799T>A' },
    { label: 'VCF', example: '7 140753336 . A T . . .' }
  ];

  // --- THE FINAL SEARCH FUNCTION WITH SIMPLIFIED ERROR HANDLING ---
  const performSearch = async (query) => {
    const searchStartTime = Date.now();
    
    setSearchState('loading');
    setLoadingProgress(0);
    setLoadingText('Connecting to service...');
    setEstimatedTime('~ 15s for new queries');

    // 1. Start a cosmetic 15-second progress bar for instant feedback
    let progress = 0;
    let messageIndex = 0;
    const totalSimulationTime = 15000;
    const intervalTime = totalSimulationTime / 100;

    if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);

    loadingIntervalRef.current = setInterval(() => {
        progress += 1;
        setLoadingProgress(Math.min(progress, 99));
        
        const newMessageIndex = Math.floor(progress / (100 / loadingMessages.length));
        if (newMessageIndex !== messageIndex && newMessageIndex < loadingMessages.length) {
            messageIndex = newMessageIndex;
            setLoadingText(loadingMessages[messageIndex]);
        }
    }, intervalTime);

    // 2. Start the real API call within a try/catch block
    try {
      const data = await serviceRequest('oncorisk', getCookieId(), '/deep-search', {
        method: 'POST',
        body: JSON.stringify({ searchTerm: query }),
      });

      // Stop the cosmetic loader as soon as the real response arrives
      if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
      setLoadingProgress(100);

      console.log("✅ API Response Received:", data);

      // Set state with real data from the successful response
      setIsCached(data.is_cached);
      setSearchResults(data);

      if (data.is_cached) {
        // High precision for cached results
        const frontendDuration = Date.now() - searchStartTime;
        setEstimatedTime(`${(frontendDuration / 1000).toFixed(3)}s`);
      } else {
        // Accurate backend time for fresh results
        setEstimatedTime(`${(data.execution_time_ms / 1000).toFixed(1)}s`);
      }

      // Animate to the results view with staggered panels
      setIsAnimating(true);
      setShowRightPanel(false);
      setShowLeftPanel(false);
      
      setTimeout(() => {
        setSearchState('results');
        setIsAnimating(false);
        
        // Start staggered animation: right panel first, then left panel after 1 second
        setTimeout(() => {
          setShowRightPanel(true);
        }, 100);
        
        setTimeout(() => {
          setShowLeftPanel(true);
        }, 1100);
      }, 500);

    } catch (error) {
      if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current); // Ensure loader stops
      console.error('Deep search failed:', error);

      // Only a variant the annotation service does not know is "no results".
      // Everything else is shown as it is: an rsID with several alleles lists
      // them, and an unavailable provider or service says so.
      const code = error?.code || null;
      const candidates = Array.isArray(error?.body?.details?.candidates)
        ? error.body.details.candidates : [];
      const notFound = ['VARIANT_NOT_FOUND', 'TRANSCRIPT_NOT_FOUND'].includes(code);
      let message = null;
      if (!notFound) {
        message = error?.status
          ? (error.message || 'The search could not be completed.')
          : 'The OncoRisk service could not be reached. Please try again in a minute.';
      }
      setSearchResults({ is_no_results: true, query, code, message, candidates });
      setSearchState('results');
    }
  };

  const handleCandidateClick = (hgvs) => {
    setSearchQuery(hgvs);
    performSearch(hgvs);
  };

  const handleSearch = (e) => { /* Unchanged */ e.preventDefault(); if (searchQuery.trim()) { performSearch(searchQuery); } };
  const handleSuggestionClick = (suggestion) => { /* Unchanged */ setSearchQuery(suggestion); performSearch(suggestion); };
  const handleSearchInputChange = (e) => { /* Unchanged */ setSearchQuery(e.target.value); if (searchState !== 'loading' && searchState !== 'initial') { setSearchState('initial'); setSearchResults(null); }};
  const getFindingsCount = () => { /* Unchanged */ if (!searchResults?.decision_support) return 0; const { therapeutic_implications, associated_diseases } = searchResults.decision_support; return (therapeutic_implications?.oncokb_fda_approved_treatments?.length || 0) + (therapeutic_implications?.civic_literature_evidence?.length || 0) + (associated_diseases?.length || 0); };

  const handleExportData = () => {
    if (!searchResults) return;
    const exportData = {
      query: searchQuery,
      timestamp: new Date().toISOString(),
      isCached,
      executionTime: estimatedTime,
      ...searchResults
    };
    const dataStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `deep_query_${searchQuery.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // The outcome of a search that returned no result: no match, a choice between
  // the alleles of a multi-allelic rsID, or the reason the search failed.
  const NoResultsView = ({ query, message, candidates = [] }) => (
    <div className="OCRP-QuerySection-loading-view">
        <div className="OCRP-QuerySection-no-results-container">
          {candidates.length > 0 ? (
            <>
              <h3>Choose one allele</h3>
              <p>"<strong>{query}</strong>" names several alternate alleles. Pick the exact variant to analyse:</p>
              <div className="OCRP-QuerySection-suggestion-list">
                {candidates.filter((candidate) => candidate.hgvsc).map((candidate) => (
                  <button
                    type="button"
                    key={candidate.hgvsc}
                    className="OCRP-QuerySection-suggestion-item"
                    onClick={() => handleCandidateClick(candidate.hgvsc)}
                  >
                    {candidate.gene} {candidate.alteration || candidate.allele} ({candidate.hgvsc})
                  </button>
                ))}
              </div>
            </>
          ) : message ? (
            <>
              <h3>Search unavailable</h3>
              <p>Your search for "<strong>{query}</strong>" could not be completed.</p>
              <p>{message}</p>
            </>
          ) : (
            <>
              <h3>No Results Found</h3>
              <p>Your search for "<strong>{query}</strong>" did not return any results.</p>
              <p>Please check the identifier or try a different query.</p>
            </>
          )}
        </div>
    </div>
  );

  return (
    <div className="OCRP-QuerySection-container">
      {/* Search Header - Original structure restored */}
      <div className={`OCRP-QuerySection-header ${searchState === 'results' ? 'OCRP-QuerySection-header-compact' : ''}`}>
        {searchState === 'results' && searchResults && !searchResults.is_no_results && (
          <div className="OCRP-QuerySection-header-left">
            <span className="OCRP-QuerySection-results-summary">
              {getFindingsCount()} findings across 3 categories
            </span>
          </div>
        )}
        
        <form onSubmit={handleSearch} className="OCRP-QuerySection-search-form">
          <div className="OCRP-QuerySection-search-wrapper">
            <i className="fas fa-search OCRP-QuerySection-search-icon"></i>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchInputChange}
              placeholder="Enter HGVS notation or rsID..."
              className="OCRP-QuerySection-search-input"
            />
            {searchQuery && (
              <button type="button" onClick={() => { setSearchQuery(''); setSearchState('initial'); setSearchResults(null); }} className="OCRP-QuerySection-clear-btn">
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </form>
        
        {searchState === 'results' && searchResults && !searchResults.is_no_results && (
          <div className="OCRP-QuerySection-header-right">
            <div className="OCRP-QuerySection-search-meta">
              <span className="OCRP-QuerySection-search-time"><i className="fas fa-clock"></i>{estimatedTime}</span>
              <span className="OCRP-QuerySection-cache-status"><i className={`fas ${isCached ? 'fa-bolt' : 'fa-database'}`}></i>{isCached ? 'Cached' : 'Fresh'}</span>
              <button className="OCRP-QuerySection-export-btn" onClick={handleExportData}>
                <i className="fas fa-download"></i> Export Data
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Initial Search View */}
      {searchState === 'initial' && (
        <div className="OCRP-QuerySection-initial-view">
          {/* Format hints */}
          <div className="OCRP-QuerySection-formats-hint">
            <span className="OCRP-QuerySection-formats-hint-title">Example search formats</span>
            {allowedFormats.map((format, index) => (
              <div key={index} className="OCRP-QuerySection-format-example">
                <span className={`OCRP-QuerySection-format-label OCRP-QuerySection-format-label-${format.label.toLowerCase()}`}>{format.label}</span>
                <span className="OCRP-QuerySection-format-value">{format.example}</span>
              </div>
            ))}
          </div>

          <div className="OCRP-QuerySection-suggestions">
            <h3>Search Suggestions</h3>
            <div className="OCRP-QuerySection-suggestion-list">
              {searchSuggestions.map((suggestion, index) => (
                <button key={index} onClick={() => handleSuggestionClick(suggestion)} className="OCRP-QuerySection-suggestion-item">
                  <i className="fas fa-lightbulb"></i>
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
          <div className="OCRP-QuerySection-separator"></div>
          <div className="OCRP-QuerySection-recent">
            <h3>Recent Searches</h3>
            <div className="OCRP-QuerySection-recent-list">
              {recentSearches.map((recent, index) => (
                <button key={index} onClick={() => handleSuggestionClick(recent)} className="OCRP-QuerySection-recent-item">
                  <i className="fas fa-history"></i>
                  {recent}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Loading View */}
      {searchState === 'loading' && (
        <div className="OCRP-QuerySection-loading-view">
          <div className="OCRP-QuerySection-loading-container">
            <div className="OCRP-QuerySection-loading-spinner"><div className="OCRP-QuerySection-spinner"></div></div>
            <h3>Analyzing your query...</h3><p className="OCRP-QuerySection-loading-text">{loadingText}</p>
            <div className="OCRP-QuerySection-progress-container">
              <div className="OCRP-QuerySection-progress-bar"><div className="OCRP-QuerySection-progress-fill" style={{ width: `${loadingProgress}%` }}></div></div>
              <span className="OCRP-QuerySection-progress-text">{Math.round(loadingProgress)}%</span>
            </div>
            <div className="OCRP-QuerySection-search-info">
              <div className="OCRP-QuerySection-info-item"><i className="fas fa-clock"></i><span>Estimated time: {estimatedTime}</span></div>
              <div className="OCRP-QuerySection-info-item"><i className={`fas fa-database`}></i><span>{loadingProgress < 5 ? 'Checking cache...' : 'Processing fresh data'}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Results View - Now handles both success and "no results" */}
      {searchState === 'results' && searchResults && (
        searchResults.is_no_results ? (
          <NoResultsView
            query={searchResults.query}
            message={searchResults.message}
            candidates={searchResults.candidates}
          />
        ) : (
          <div className={`OCRP-QuerySection-results-view ${isAnimating ? 'OCRP-QuerySection-animating' : ''}`}>
            <div className="OCRP-QuerySection-results-body">
            <div className={`OCRP-QuerySection-body-left ${showLeftPanel ? 'OCRP-QuerySection-panel-visible' : 'OCRP-QuerySection-panel-hidden'}`}>
              <BodyVisual searchResults={searchResults} />
            </div>
            <div className={`OCRP-QuerySection-body-right ${showRightPanel ? 'OCRP-QuerySection-panel-visible' : 'OCRP-QuerySection-panel-hidden'}`}>
              <QueryResults searchResults={searchResults} />
            </div>
            </div>
          </div>
        )
      )}
    </div>
  );
}

export default Query;
